import { useEffect, useMemo, useRef, useState } from "react";

import type { Extent, Viewport } from "../../lib/chartViewport";
import { clampViewport } from "../../lib/chartViewport";
import { seriesKey } from "../../lib/chartUtils";
import { BRAND_COLORS, hexToRgba } from "../../lib/brandColors";
import type { TemperatureSeries } from "../../types";
import TimeSeriesChart, { type TimeSeriesSeries } from "../charts/TimeSeriesChart";

type AnalysisChartProps = {
  series: TemperatureSeries[];
  valueDomain: readonly [number, number];
  colorPalette: readonly string[];
  safeRange: { min: number; max: number };
};

export function AnalysisChart({ series, valueDomain, colorPalette, safeRange }: AnalysisChartProps) {
  const [viewport, setViewport] = useState<Viewport | null>(null);
  const previousSelectionKey = useRef("");
  const hourFormatter = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }),
    [],
  );

  const chartSeries = useMemo<TimeSeriesSeries[]>(() => {
    const palette = colorPalette.length ? colorPalette : ["#2563eb"];

    return series
      .map((item, index) => {
        const points = item.points
          .map((point) => {
            const hours = Number(point.hours);
            if (!Number.isFinite(hours) || !Number.isFinite(point.value)) return null;
            return { t: hours, v: point.value };
          })
          .filter((point): point is { t: number; v: number } => point !== null)
          .sort((a, b) => a.t - b.t);

        if (!points.length) return null;

        return {
          id: seriesKey(item),
          name: item.position
            ? `${formatDeviceIdentifier(item.shipmentId, item.deviceId)} (${item.position})`
            : formatDeviceIdentifier(item.shipmentId, item.deviceId),
          color: palette[index % palette.length],
          data: points,
        } satisfies TimeSeriesSeries;
      })
      .filter((item): item is TimeSeriesSeries => item !== null);
  }, [colorPalette, series]);

  const extent = useMemo<Extent | null>(() => {
    if (!chartSeries.length) return null;
    let min = Number.POSITIVE_INFINITY;
    let max = Number.NEGATIVE_INFINITY;

    for (const item of chartSeries) {
      for (const point of item.data) {
        if (point.t < min) min = point.t;
        if (point.t > max) max = point.t;
      }
    }

    if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) return null;
    return { min, max };
  }, [chartSeries]);

  const selectionKey = useMemo(() => chartSeries.map((item) => item.id).join("\n"), [chartSeries]);

  useEffect(() => {
    if (!extent) {
      setViewport(null);
      previousSelectionKey.current = "";
      return;
    }

    if (previousSelectionKey.current !== selectionKey) {
      previousSelectionKey.current = selectionKey;
      setViewport({ start: extent.min, end: extent.max });
      return;
    }

    setViewport((current) => (current ? clampViewport(current, extent) : { start: extent.min, end: extent.max }));
  }, [extent, selectionKey]);

  if (!chartSeries.length) {
    return <p>Select sensors using the table below.</p>;
  }

  return (
    <TimeSeriesChart
      series={chartSeries}
      height={500}
      yLabel="Temperature (°C)"
      yDomain={valueDomain}
      extent={extent}
      viewport={viewport}
      onViewportChange={setViewport}
      unitLabel="°C"
      safeBand={{ min: safeRange.min, max: safeRange.max, color: "#bbf7d0", opacity: 0.25 }}
      xAxisType="value"
      xAxisName="Time (h)"
      tooltipXFormatter={(value) => `${hourFormatter.format(value)} h`}
      showLegend={false}
      gridBottom={74}
      dataZoomSlider={{
        bottom: 2,
        height: 24,
        fillerColor: hexToRgba(BRAND_COLORS.blueLight, 0.34),
        backgroundColor: hexToRgba(BRAND_COLORS.blueLight, 0.14),
        borderColor: hexToRgba(BRAND_COLORS.blueDark, 0.22),
        dataBackgroundLineColor: hexToRgba(BRAND_COLORS.blueDark, 0.45),
        dataBackgroundAreaColor: hexToRgba(BRAND_COLORS.blueLight, 0.24),
        selectedDataBackgroundLineColor: hexToRgba(BRAND_COLORS.blueDark, 0.7),
        selectedDataBackgroundAreaColor: hexToRgba(BRAND_COLORS.blueLight, 0.34),
        handleColor: BRAND_COLORS.blueLight,
        handleBorderColor: BRAND_COLORS.blueDark,
        moveHandleColor: hexToRgba(BRAND_COLORS.blueDark, 0.12),
      }}
    />
  );
}

function formatDeviceIdentifier(shipmentId: string, rawDeviceId: string | null | undefined): string {
  const normalized = (rawDeviceId ?? "").trim();
  if (!normalized) return "—";
  const prefix = `${shipmentId}-`;
  if (normalized.startsWith(prefix)) {
    return normalized.slice(prefix.length) || normalized;
  }
  return normalized;
}
